import React from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { getAboutUsHeader, getAboutUsParagraph } from "../../api/axios";
import useSWR from "swr";
import Link from "@mui/material/Link";
import { useTheme } from "@mui/material/styles";
import {getDataByPageAndPosition} from "../../Frontend/Components/Common/common"

// Styled Components
const Container = styled.div`
  width: 100%;
`;

const Wrapper = styled.div`
  padding: 50px 0px 0px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 20px;
  max-width: 1536px;
  margin: 0 auto;
  padding-left: 20px;
  padding-right: 20px;
  transition: all 0.3s ease-out;

  @media (max-width: 1200px) {
    padding: 20px 20px 0px;
    grid-gap: 15px;
  }

  @media (max-width: 1024px) {
    padding: 10px 20px 0px;
    grid-template-rows: auto;
  }

  @media (max-width: 768px) {
    padding: 0px 15px 0px;
    grid-gap: 10px;
  }

  @media (max-width: 650px) {
    display: grid;
    padding: 10px 15px 0px;
    grid-template-columns: repeat(1, 1fr);
    grid-gap: 15px;
  }

  @media (max-width: 480px) {
    grid-gap: 12px;
  }

  @media (max-width: 360px) {
    padding: 5px 8px 0px;
    grid-gap: 10px;
  }
`;

const TitleWrapper = styled.div`
  padding-top: 0;
  font-weight: 500;
  font-size: 50px;
  line-height: 129.4%;
  text-shadow: 1px 0px 10px rgba(0, 0, 0, 0.15);
  color: ${props => props.theme?.palette?.text?.main || '#000000'};
  transition: all 0.3s ease-out;
  animation: fadeInUp 0.8s ease-out;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.6s ease-out;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    text-shadow: 0px 6px 15px rgba(0, 0, 0, 0.25);
    transform: translateY(-2px);
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 1200px) {
    font-size: 45px;
    line-height: 125%;
  }

  @media (max-width: 1024px) {
    font-size: 42px;
    line-height: 120%;
  }

  @media (max-width: 768px) {
    font-size: 38px;
    line-height: 118%;
  }

  @media (max-width: 650px) {
    font-size: 32px;
    line-height: 115%;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 28px;
    line-height: 112%;
  }

  @media (max-width: 360px) {
    font-size: 24px;
    line-height: 110%;
  }
`;

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-end;
  transition: all 0.3s ease-out;
  animation: fadeInRight 0.8s ease-out 0.2s both;

  @keyframes fadeInRight {
    from {
      opacity: 0;
      transform: translateX(30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @media (max-width: 768px) {
    gap: 15px;
  }

  @media (max-width: 650px) {
    align-items: center;
    gap: 12px;
    animation: fadeInUp 0.8s ease-out 0.2s both;
  }

  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const Paragraph = styled.p`
  padding-top: 0;
  font-style: normal;
  font-weight: 400;
  font-size: 23px;
  line-height: 38px;
  text-align: right !important;
  letter-spacing: 0.039em;
  color: #000000;
  font-weight: bold;
  margin: 0;
  transition: all 0.3s ease-out;
  position: relative;
  animation: fadeInUp 0.8s ease-out 0.4s both;

  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    right: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, ${props => props.theme?.palette?.primary?.main || '#FF8533'}, transparent);
    transition: width 0.4s ease-out;
  }

  &:hover::after {
    width: 100%;
  }

  &:hover {
    text-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 1200px) {
    font-size: 22px;
    line-height: 36px;
  }

  @media (max-width: 1024px) {
    font-size: 20px;
    line-height: 34px;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 30px;
  }

  @media (max-width: 650px) {
    font-size: 16px;
    line-height: 28px;
    text-align: justify !important;
    text-align-last: center;
    
    &::after {
      right: auto;
      left: 50%;
      transform: translateX(-50%);
    }
  }

  @media (max-width: 480px) {
    font-size: 15px;
    line-height: 26px;
    text-align: left !important;
    
    &::after {
      left: 0;
      transform: none;
    }
  }

  @media (max-width: 360px) {
    font-size: 14px;
    line-height: 24px;
  }
`;

const StyledLink = styled(Link)`
  background: ${props => props.theme?.palette?.primary?.main || '#FF8533'} !important;
  border: 2px solid ${props => props.theme?.palette?.primary?.main || '#FF8533'} !important;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.15);
  border-radius: 30px;
  font-size: 23px;
  color: white !important;
  text-shadow: 0px 4px 6px rgba(0, 0, 0, 0.15);
  padding: 12px 24px;
  cursor: pointer;
  outline: none;
  transition: all 0.3s ease-out;
  text-decoration: none;
  font-weight: bold;
  display: inline-block;
  text-align: center;
  min-width: 120px;

  &:hover {
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.25);
    transform: translateY(-3px);
    background: ${props => props.theme?.palette?.primary?.dark || '#e67300'} !important;
    border-color: ${props => props.theme?.palette?.primary?.dark || '#e67300'} !important;
  }

  &:active {
    transform: translateY(-1px);
    transition: all 0.1s ease-out;
  }

  @media (max-width: 1200px) {
    font-size: 22px;
    padding: 11px 22px;
  }

  @media (max-width: 1024px) {
    font-size: 20px;
    padding: 10px 20px;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    padding: 9px 18px;
    min-width: 110px;
  }

  @media (max-width: 650px) {
    font-size: 16px;
    padding: 8px 16px;
    min-width: 100px;
  }

  @media (max-width: 480px) {
    font-size: 15px;
    padding: 7px 14px;
    min-width: 90px;
  }

  @media (max-width: 360px) {
    font-size: 14px;
    padding: 6px 12px;
    min-width: 80px;
  }
`;

function AUUpper({ button }) {
  let navigate = useNavigate();
  const theme = useTheme();
  const aboutUsHeaderButton = getDataByPageAndPosition(button || [], "About Us", "Header section") || [];

  const { data: header } = useSWR(
    `aboutUsHeader`,
    async () => {
      try {
        const { data } = await getAboutUsHeader();
        return data?.data?.[0]?.paragraph || [];
      } catch (error) {
        console.error("error: ", error);
      }
    },
    {
      revalidateOnFocus: false,
    }
  );

  const { data: headerParagraph } = useSWR(
    `aboutUsParagraph`,
    async () => {
      try {
        const { data } = await getAboutUsParagraph();
        return data?.data?.[0]?.paragraph || [];
      } catch (error) {
        console.error("error: ", error);
      }
    },
    {
      revalidateOnFocus: false,
    }
  );

  return (
    <Container>
      <Wrapper>
        <TitleWrapper theme={theme}>{header}</TitleWrapper>
        <BodyWrapper>
          <Paragraph>{headerParagraph}</Paragraph>
          {aboutUsHeaderButton?.length > 0 && (
            <StyledLink
              theme={theme}
              underline="none"
              onClick={() => {
                navigate(aboutUsHeaderButton[0]?.buttonLink);
              }}
            >
              {aboutUsHeaderButton[0]?.button_name}
            </StyledLink>
          )}
        </BodyWrapper>
      </Wrapper>
    </Container>
  );
}

export default AUUpper;
